import { ModuleColumnDefinition, ModuleTypeEnum } from '../module';
import { FieldTypeEnum } from '../custom-field.types';
import { EntityTerminology } from '../organization-settings';
import { STANDARD_COLUMN_WIDTHS, getFieldTypeWidth } from '../column-widths';

/**
 * Creates standard column definitions for the Deal module with proper terminology
 * @param terminology - The entity terminology to use for labels
 */
export function createDealStandardColumns(terminology?: EntityTerminology): ModuleColumnDefinition[] {
  const entityName = terminology?.singular || 'client';
  const EntityName = entityName.charAt(0).toUpperCase() + entityName.slice(1);

  return [
    // System columns (always visible)
    {
      id: 'name',
      name: 'name',
      label: 'Deal Name',
      fieldType: FieldTypeEnum.enum.STRING,
      isSystem: true,
      isFixed: true,
      isVisible: true,
      isRequired: true,
      order: 1,
      ...STANDARD_COLUMN_WIDTHS.VERY_WIDE
    },
    
    // Standard columns
    {
      id: 'primaryContactId',
      name: 'primaryContactId',
      label: 'Contact',
      description: 'Primary contact associated with this deal',
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      isSystem: false,
      isFixed: false,
      isVisible: true,
      isRequired: false,
      order: 2,
      ...STANDARD_COLUMN_WIDTHS.WIDE
    },
    {
      id: 'entityId',
      name: 'entityId',
      label: EntityName,
      description: `${EntityName} associated with this deal`,
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      isSystem: false,
      isFixed: false,
      isVisible: true,
      isRequired: false,
      order: 3,
      ...STANDARD_COLUMN_WIDTHS.WIDE
    },
    {
      id: 'description',
      name: 'description',
      label: 'Description',
      fieldType: FieldTypeEnum.enum.STRING,
      isSystem: false,
      isFixed: false,
      isVisible: true,
      isRequired: false,
      order: 4,
      ...STANDARD_COLUMN_WIDTHS.WIDE
    },
    {
      id: 'amount',
      name: 'amount',
      label: 'Amount',
      fieldType: FieldTypeEnum.enum.NUMBER,
      isSystem: false,
      isFixed: false,
      isVisible: true,
      isRequired: false,
      order: 5,
      ...STANDARD_COLUMN_WIDTHS.NARROW
    },
    {
      id: 'probability',
      name: 'probability',
      label: 'Confidence',
      description: "The rep's call on how likely this deal is to close. Compare it with Win Rate to see where instinct and history disagree.",
      fieldType: FieldTypeEnum.enum.NUMBER,
      isSystem: false,
      isFixed: false,
      isVisible: true,
      isRequired: false,
      order: 6,
      ...STANDARD_COLUMN_WIDTHS.NARROW
    },
    {
      // T6 — historical win-rate for this deal's canonical stage (the base rate).
      // Read-only; UniversalField renders it specially. Hidden by default to keep
      // the table lean — the Gap column (below) carries the headline signal.
      id: 'derivedProbability',
      name: 'derivedProbability',
      label: 'Win Rate',
      description: 'Historical win rate for this stage, learned from your own closed deals. Needs enough closed-deal history before it is trusted.',
      fieldType: FieldTypeEnum.enum.NUMBER,
      isSystem: false,
      isFixed: false,
      isVisible: false,
      isRequired: false,
      order: 7,
      ...STANDARD_COLUMN_WIDTHS.NARROW
    },
    {
      // T7 — rep confidence − derived probability. The forecast signal: large
      // gaps (either direction) are flagged. Read-only; rendered specially.
      id: 'derivedProbabilityDelta',
      name: 'derivedProbabilityDelta',
      label: 'Gap',
      description: 'Confidence minus win rate. Large gaps in either direction are flagged. Click a cell to see the confidence history.',
      fieldType: FieldTypeEnum.enum.NUMBER,
      isSystem: false,
      isFixed: false,
      isVisible: true,
      isRequired: false,
      order: 8,
      // Signed gap: no-signal deals (null) must sink to the bottom, not sort as
      // 0 between the negative and positive divergences. Mirrors the backend's
      // nulls-last ordering for the server-side path.
      sortNullsLast: true,
      // MEDIUM (not NARROW): the cell shows the delta plus its two inputs
      // ("↗ +40  90 vs 50") and needs the extra room.
      ...STANDARD_COLUMN_WIDTHS.MEDIUM
    },
    {
      id: 'stage',
      name: 'stage',
      label: 'Stage',
      description: 'Deal pipeline stage',
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      isSystem: false,
      isFixed: false,
      isVisible: true,
      isRequired: false,
      order: 9,
      ...STANDARD_COLUMN_WIDTHS.MEDIUM
    },
    {
      // T11 — deal-shape type. Drives the rough deliverable mix demand is seeded
      // from (T14). Fixed-option dropdown so it renders + edits inline.
      id: 'dealType',
      name: 'dealType',
      label: 'Deal Type',
      description: 'The work shape of the deal. Drives the capacity forecast and demand planning.',
      fieldType: FieldTypeEnum.enum.DROPDOWN,
      isSystem: false,
      isFixed: false,
      isVisible: true,
      isRequired: false,
      order: 16,
      options: [
        { value: 'SERVICES', label: 'Services' },
        { value: 'RETAINER', label: 'Retainer' },
        { value: 'LICENSING', label: 'Licensing' },
        { value: 'SUBSCRIPTION', label: 'Subscription' },
        { value: 'PRODUCT', label: 'Product' },
        { value: 'MIXED', label: 'Mixed' },
      ],
      ...STANDARD_COLUMN_WIDTHS.MEDIUM
    },
    {
      id: 'tags',
      name: 'tags',
      label: 'Tags',
      description: 'Tags for organizing and categorizing the deal',
      fieldType: FieldTypeEnum.enum.STRING,
      isSystem: true,
      isFixed: false,
      isVisible: true,
      isRequired: false,
      order: 10,
      ...STANDARD_COLUMN_WIDTHS.WIDE
    },
    {
      id: 'closeDate',
      name: 'closeDate',
      label: 'Close Date',
      fieldType: FieldTypeEnum.enum.DATE,
      isSystem: false,
      isFixed: false,
      isVisible: true,
      isRequired: false,
      order: 11,
      ...STANDARD_COLUMN_WIDTHS.MEDIUM
    },
    {
      id: 'expectedStartDate',
      name: 'expectedStartDate',
      label: 'Expected Start',
      description: 'When work is expected to begin if the deal lands — anchors resource planning',
      fieldType: FieldTypeEnum.enum.DATE,
      isSystem: false,
      isFixed: false,
      isVisible: true,
      isRequired: false,
      order: 17,
      ...STANDARD_COLUMN_WIDTHS.MEDIUM
    },
    {
      id: 'assignedToId',
      name: 'assignedToId',
      label: 'Assigned To',
      fieldType: FieldTypeEnum.enum.USER,
      isSystem: false,
      isFixed: false,
      isVisible: true,
      isRequired: false,
      order: 12,
      ...STANDARD_COLUMN_WIDTHS.MEDIUM
    },

    // Metadata columns
    {
      id: 'createdById',
      name: 'createdById',
      label: 'Created By',
      fieldType: FieldTypeEnum.enum.USER,
      isSystem: true,
      isFixed: false,
      isVisible: false,
      isRequired: false,
      order: 13,
      ...STANDARD_COLUMN_WIDTHS.MEDIUM
    },
    {
      id: 'createdAt',
      name: 'createdAt',
      label: 'Created',
      fieldType: FieldTypeEnum.enum.DATE,
      isSystem: false,
      isFixed: false,
      isVisible: false,
      isRequired: false,
      order: 14,
      ...STANDARD_COLUMN_WIDTHS.MEDIUM
    },
    {
      id: 'updatedAt',
      name: 'updatedAt',
      label: 'Updated',
      fieldType: FieldTypeEnum.enum.DATE,
      isSystem: false,
      isFixed: false,
      isVisible: false,
      isRequired: false,
      order: 15,
      ...STANDARD_COLUMN_WIDTHS.MEDIUM
    }
  ];
}

/**
 * Default standard column definitions for the Deal module
 * Uses default 'client' terminology
 */
export const DEAL_STANDARD_COLUMNS: ModuleColumnDefinition[] = createDealStandardColumns();

/**
 * Helper to combine standard and custom fields
 */
export function getDealColumns(
  customFields: any[] = [],
  visibilityPreferences?: Record<string, boolean>,
  terminology?: EntityTerminology
): ModuleColumnDefinition[] {
  const standardColumns = (terminology ? createDealStandardColumns(terminology) : DEAL_STANDARD_COLUMNS).map(column => ({
    ...column,
    isVisible: visibilityPreferences?.[column.id] !== undefined
      ? !!visibilityPreferences[column.id]
      : column.isVisible,
  }));

  const customColumns = customFields.map((field, index) => {
    const widthSettings = getFieldTypeWidth(field.fieldType);

    // Parse dropdown options from settings if available
    const fieldOptions = (field.fieldType === 'DROPDOWN' || field.fieldType === 'MULTISELECT')
      && field.settings?.options && Array.isArray(field.settings.options)
      ? field.settings.options.map((opt: any) => {
          if (typeof opt === 'string') return { value: opt, label: opt };
          return { value: String(opt.value || opt.id || opt.label || ''), label: String(opt.label || opt.name || opt.value || '') };
        })
      : undefined;

    return {
      id: `custom_${field.fieldName}`,
      name: field.fieldName,
      label: field.label,
      description: `Custom field: ${field.label}`,
      isVisible: visibilityPreferences?.[`custom_${field.fieldName}`] !== undefined
        ? !!visibilityPreferences[`custom_${field.fieldName}`]
        : !!field.isActive,
      isRequired: false,
      isSystem: false,
      order: 91 + index,
      fieldType: field.fieldType,
      width: widthSettings.width,
      minWidth: widthSettings.minWidth,
      ...(fieldOptions ? { options: fieldOptions } : {}),
    };
  }) as ModuleColumnDefinition[];

  return [...standardColumns, ...customColumns];
}

export function getDealModuleType(): ModuleTypeEnum {
  return ModuleTypeEnum.DEAL;
}
